import {isInaccessible} from 'graphql-client/InaccessibleInfo';

import {
  ClinicalBasedStudyExecutionOverviewQuery_study_StudyNode_sampleSummary_SampleSummaryNode as SampleSummaryNode,
  ClinicalBasedStudyExecutionOverviewQuery_study_StudyNode_sampleSummary_SampleSummaryNode_sampleDefinitionSummaries_SampleDefinitionSummaryNode_testOrderSummaries_TestOrderSummaryNode as SampleDefinitionTestOrderSummaryNode,
  ClinicalBasedStudyExecutionOverviewQuery_study_StudyNode_sampleSummary_SampleSummaryNode_sampleDefinitionSummaries_SampleDefinitionSummaryNode_testOrderSummaries_TestOrderSummaryNode_procedure as ProcedureOrInaccessibleInfoNode,
  ClinicalBasedStudyExecutionOverviewQuery_study_StudyNode_studyDesign_TrialSampleTestingStudyDesignNode_sampleTestPlans_StudySampleTestPlanNode as SampleTestPlanNode,
  ClinicalBasedStudyExecutionOverviewQuery_study_StudyNode_studyDesign_TrialSampleTestingStudyDesignNode_sampleTestPlans_StudySampleTestPlanNode_procedure_ProcedureNode as ProcedureNode,
} from './__generated/ClinicalBasedStudyExecutionOverviewQuery';
import {getPublishedProcedureFlowchartVersionId} from './PublishedProcedureFlowchartVersionUtils';
export interface SampleDefinitionTestOrderSummary {
  assay: ProcedureOrInaccessibleInfoNode;
  resolvedCount: number;
  totalCount: number;
}

export interface SampleDefinitionReceivingSummary {
  receivedCount: number;
  discrepancyCount: number;
  stillTargetedCount: number;
}

export interface SampleDefinitionSummary {
  sampleDefinitionId: string;
  sampleDefinitionLabel: string;
  receiving: SampleDefinitionReceivingSummary;
  testOrderSummaries: Array<SampleDefinitionTestOrderSummary>;
}

export interface SampleTestPlanProcedure {
  procedureId: string;
  publishedProcedureFlowchartVersionId: string | null;
}

export const getSampleDefinitionTestOrderSummaries = (
  testOrderSummaries: Array<SampleDefinitionTestOrderSummaryNode>
): Array<SampleDefinitionTestOrderSummary> =>
  testOrderSummaries.map((testOrderSummaryNode) => ({
    assay: testOrderSummaryNode.procedure,
    resolvedCount: testOrderSummaryNode.resolvedCount,
    totalCount: testOrderSummaryNode.totalCount,
  }));

export const getSampleDefinitionSummaries = (
  sampleSummaryNode: SampleSummaryNode | null
): Array<SampleDefinitionSummary> => {
  if (!sampleSummaryNode) {
    return [];
  }
  const sampleDefinitionSummaryNodes = sampleSummaryNode.sampleDefinitionSummaries;

  return sampleDefinitionSummaryNodes.map((sampleDefinitionSummaryNode) => ({
    sampleDefinitionId: sampleDefinitionSummaryNode.sampleDefinition.id,
    sampleDefinitionLabel: sampleDefinitionSummaryNode.sampleDefinition.label,
    receiving: {
      receivedCount: sampleDefinitionSummaryNode.receivedCount,
      discrepancyCount: sampleDefinitionSummaryNode.discrepancyCount ?? 0,
      stillTargetedCount:
        sampleDefinitionSummaryNode.sampleDefinition.treatmentGroup.targetNumberOfSubjects -
        sampleDefinitionSummaryNode.receivedCount,
    },
    testOrderSummaries: getSampleDefinitionTestOrderSummaries(sampleDefinitionSummaryNode.testOrderSummaries),
  }));
};

export const getUncategorizedSampleDefinitionSummary = (
  sampleSummaryNode: SampleSummaryNode | null
): SampleDefinitionReceivingSummary | null => {
  if (
    !sampleSummaryNode ||
    !sampleSummaryNode.uncategorizedSampleDefinitionSummary ||
    sampleSummaryNode.uncategorizedSampleDefinitionSummary.receivedCount === 0
  ) {
    return null;
  }
  const {receivedCount, discrepancyCount} = sampleSummaryNode.uncategorizedSampleDefinitionSummary;
  return {
    receivedCount,
    discrepancyCount,
    stillTargetedCount: 0,
  };
};

export const getSampleTestPlanProcedures = (
  sampleTestPlans: Array<SampleTestPlanNode>
): Array<SampleTestPlanProcedure> =>
  sampleTestPlans
    .filter(
      (sampleTestPlan): sampleTestPlan is SampleTestPlanNode & {procedure: ProcedureNode} =>
        !isInaccessible(sampleTestPlan.procedure)
    )
    .map((sampleTestPlan) => ({
      procedureId: sampleTestPlan.procedure.id,
      publishedProcedureFlowchartVersionId: getPublishedProcedureFlowchartVersionId(sampleTestPlan.procedure),
    }));
